import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field } from 'redux-form/immutable';
import { withRouter } from 'react-router';
import { withStyles, Divider } from '@material-ui/core';
import { get, map, isString, isArray, isObject, isFunction } from 'lodash';
import classnames from 'classnames';
import { addQuery } from '@lib/services/queryService';

import { ExpansionPanel, Typography } from 'components/v2';

import FieldComponentFilter from './FieldComponentFilter';
import FilterAppointmentStatus from './FilterAppointmentStatus';
import FilterDistance from './FilterDistance/FilterDistance';
import FilterListAppointmentTypes from './FilterListAppointmentTypes';
import FilterListFacilities from './FilterListFacilities';
import FilterListProviders from './FilterListProviders';
import FilterListUsers from './FilterListUsers';
import FilterSingleChoice from './FilterSingleChoice';

export const FILTER = {
  SINGLE_CHOICE: {
    FilterComponent: FilterSingleChoice,
    defaultExpanded: true,
    fieldName: 'singleChoiceFilter',
    key: 'singleChoiceFilter',
    label: 'Single Choice',
    respectSchedulerSetting: true,
  },
  REFERRAL_STATUS: {
    FilterComponent: FilterSingleChoice,
    defaultExpanded: true,
    fieldName: 'referralStatusFilter',
    items: [
      {
        _id: 'all',
        name: 'All',
      },
      {
        _id: 'active:=<',
        name: 'Active',
      },
      {
        _id: 'called,called-late',
        name: 'Called',
      },
      {
        _id: 'active:=>',
        name: 'Pending',
      },
      {
        _id: 'pending',
        name: 'Needs Approval',
      },
      {
        _id: 'scheduled,scheduled-late',
        name: 'Scheduled',
      },
      {
        _id: 'cancelled',
        name: 'Canceled',
      },
      {
        _id: 'incomplete',
        name: 'Incomplete',
      },
      {
        _id: 'closed',
        name: 'Closed',
      },
    ],
    key: 'referralStatusFilter',
    label: 'Status',
    respectSchedulerSetting: true,
  },
  APPOINTMENT_STATUS: {
    FilterComponent: FilterAppointmentStatus,
    defaultExpanded: true,
    fieldName: 'statusFilter',
    key: 'statusFilter',
    label: 'Status',
    respectSchedulerSetting: true,
  },
  APPOINTMENT_TYPE: {
    FilterComponent: FilterListAppointmentTypes,
    defaultExpanded: true,
    fieldName: 'appointmentTypeFilter',
    key: 'appointmentTypeFilter',
    label: 'Appointment Type',
    respectSchedulerSetting: true,
  },
  PROVIDER: {
    FilterComponent: FilterListProviders,
    defaultExpanded: true,
    fieldName: 'providerFilter',
    key: 'providerFilter',
    label: 'Provider',
    respectSchedulerSetting: true,
  },
  FACILITY: {
    FilterComponent: FilterListFacilities,
    defaultExpanded: true,
    fieldName: 'facilityFilter',
    key: 'facilityFilter',
    label: 'Facility',
    respectSchedulerSetting: true,
  },
  ASSIGNEES: {
    FilterComponent: FilterListUsers,
    defaultExpanded: true,
    fieldName: 'assignee',
    key: 'assignee',
    label: 'Assignees',
    respectSchedulerSetting: true,
  },
  DISTANCE: {
    FilterComponent: FilterDistance,
    defaultExpanded: true,
    fieldName: 'distance',
    key: 'distance',
    label: 'Distance',
    respectSchedulerSetting: true,
  },
};

const styles = ({
  palette: {
    common: { white },
  },
}) => ({
  root: {
    background: white,
    display: 'inline-block',
    height: '100%',
    width: '100%',
    '& > div:last-child > div:last-child > div:last-child': {
      borderBottom: 'none !important',
    },
  },
});

export class Filters extends React.PureComponent {
  handleChange = (field, data) => {
    const { syncQueryParams, onChange } = this.props;
    let value = isFunction(data.toJS) ? data.toJS() : data;
    if (onChange) {
      onChange(field, value);
    }
    if (isArray(value)) {
      value = value.join(',');
    }
    if (syncQueryParams) {
      addQuery(
        isObject(value)
          ? value
          : {
              [field]: value,
            },
      );
    }
  };

  renderFilter = (key, filter) => {
    const {
      defaultValues,
      syncQueryParams,
      useFormFields,
      hideSearchField,
      location: { query },
    } = this.props;
    const { fieldName, ...filterProps } = filter;
    const { FilterComponent } = filterProps;
    const name = fieldName || key;
    let value = syncQueryParams ? query[name] : defaultValues[name];
    const isGoogleFilter = filter.isGoogleFilter;
    const hasAccessToSwGQueryAllApptTypes = filter.hasAccessToSwGQueryAllApptTypes;

    if (isGoogleFilter && syncQueryParams && !hasAccessToSwGQueryAllApptTypes) {
      const firstValue = typeof query[name] === 'string' ? query[name].split(',')[0] : '';
      value = firstValue ? [firstValue] : [];
    }

    if (name === 'distance') {
      value = {
        zipcode: syncQueryParams ? get(query, 'zipcode', '') : get(defaultValues, 'distance.zipcode', ''),
        zipcodeMaxDistance: syncQueryParams
          ? get(query, 'zipcodeMaxDistance', 50)
          : get(defaultValues, 'distance.zipcodeMaxDistance', 50),
      };
    }

    if (key === FILTER.SINGLE_CHOICE.key || key === FILTER.REFERRAL_STATUS.key) {
      return (
        <FilterComponent
          value={value}
          onFilterChange={(v) => this.handleChange(name, v)}
          {...filterProps}
          hideSearchField={hideSearchField}
        />
      );
    }

    if (useFormFields) {
      return <Field component={FieldComponentFilter} name={name} hideSearchField={hideSearchField} {...filterProps} />;
    }
    return (
      <FieldComponentFilter
        value={isString(value) ? value.split(',') : value}
        onFilterChange={(v) => this.handleChange(name, v)}
        hideSearchField={hideSearchField}
        {...filterProps}
      />
    );
  };

  render() {
    const { classes, className, filters } = this.props;

    return (
      <div className={classnames(classes.root, className || '')}>
        {map(filters, ({ key, label, defaultExpanded, ...other }) => (
          <React.Fragment key={`filter-fragment-${key}`}>
            <ExpansionPanel
              key={key}
              summary={<Typography variant="sectionHeader">{label}</Typography>}
              defaultExpanded={defaultExpanded}
            >
              {this.renderFilter(key, other)}
            </ExpansionPanel>
            <Divider />
          </React.Fragment>
        ))}
      </div>
    );
  }
}

Filters.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  location: PropTypes.object.isRequired,
  filters: PropTypes.array.isRequired,
  defaultValues: PropTypes.object,
  onChange: PropTypes.func,
  syncQueryParams: PropTypes.bool,
  useFormFields: PropTypes.bool,
};

Filters.defaultProps = {
  defaultValues: {},
  onChange: undefined,
  syncQueryParams: false,
  useFormFields: false,
};

export default compose(withStyles(styles), withRouter)(Filters);
